<template>
  <div class="floating-alert-container">

    <!-- Progress Alert -->
    <transition name="slide">
      <div v-if="progress.active" class="floating-alert">
        <div class="progress-alert shadow">

          <!-- X button top-right when done or failed -->
          <button v-if="progress.done || progress.failed" class="progress-close-btn" @click="closeProgress">
            <font-awesome-icon icon="xmark" />
          </button>

          <!-- Left: message + step + fail message + countdown -->
          <div class="progress-alert__left">
            <span class="progress-alert__message">{{ progress.message }}</span>
            <span v-if="progress.step" class="progress-alert__step">{{ progress.step }}</span>
            <span v-if="progress.failed" class="progress-alert__fail">{{ progress.failMessage }}</span>
            <span v-if="progress.done || progress.failed" class="progress-alert__countdown">Closing in {{ progress.countdown }}s...</span>
          </div>

          <!-- Right: circle -->
          <div class="progress-alert__right">
            <svg class="circle-svg" viewBox="0 0 36 36">
              <circle class="circle-track" cx="18" cy="18" r="15.9" />
              <circle
                class="circle-fill"
                :class="progress.failed ? 'circle-fill--failed' : progress.done ? 'circle-fill--done' : ''"
                cx="18" cy="18" r="15.9"
                :stroke-dasharray="`${progress.value} ${100 - progress.value}`"
              />
            </svg>
            <span
              class="circle-percent"
              :class="progress.failed ? 'percent-failed' : progress.done ? 'percent-done' : ''"
            >{{ progress.value }}%</span>
          </div>

        </div>
      </div>
    </transition>

    <!-- Regular Alerts -->
    <transition-group name="slide" tag="div">
      <div v-for="alert in alerts" :key="alert.id" class="floating-alert">
        <CAlert
          :color="alert.type"
          :show="true"
          closeButton
          @update:show="removeAlert(alert.id)"
          class="mb-0 shadow"
        >
          <font-awesome-icon v-if="alert.icon" :icon="alert.icon" class="mr-2" />
          <strong v-if="alert.title" class="mr-1">{{ alert.title }}</strong>
          {{ alert.message }}
        </CAlert>
      </div>
    </transition-group>

  </div>
</template>

<script>
export default {
  name: 'FloatingAlert',

  data() {
    return {
      alerts: [],
      counter: 0,
      progress: {
        active:         false,
        done:           false,
        failed:         false,
        value:          0,
        message:        'Saving...',
        step:           '',
        failMessage:    '',
        total:          3,
        current:        0,
        countdown:      5,
        _countdownTimer: null,
      },
    }
  },

  methods: {

    // ── Regular Alerts ────────────────────────────────────────────────

    add({ message, type = 'info', title = '', icon = '', timeout = 4000 }) {
      const id = ++this.counter
      this.alerts.push({ id, message, type, title, icon })
      if (timeout > 0) setTimeout(() => this.removeAlert(id), timeout)
    },

    removeAlert(id) {
      const idx = this.alerts.findIndex(a => a.id === id)
      if (idx !== -1) this.alerts.splice(idx, 1)
    },

    success(msg, opts = {}) { this.add({ message: msg, type: 'success', icon: 'circle-check',        ...opts }) },
    error(msg, opts = {})   { this.add({ message: msg, type: 'danger',  icon: 'circle-xmark',         ...opts }) },
    warning(msg, opts = {}) { this.add({ message: msg, type: 'warning', icon: 'triangle-exclamation', ...opts }) },
    info(msg, opts = {})    { this.add({ message: msg, type: 'info',    icon: 'circle-info',           ...opts }) },

    // ── Progress Alerts ───────────────────────────────────────────────

    progressStart(total = 3, message = 'Saving...') {
      clearInterval(this.progress._countdownTimer)
      this.progress.active        = true
      this.progress.done          = false
      this.progress.failed        = false
      this.progress.failMessage   = ''
      this.progress.value         = 0
      this.progress.current       = 0
      this.progress.total         = total
      this.progress.message       = message
      this.progress.step          = ''
      this.progress.countdown     = 5
    },

    progressStep(stepLabel = '') {
      this.progress.current = Math.min(this.progress.current + 1, this.progress.total)
      this.progress.value   = Math.round((this.progress.current / this.progress.total) * 100)
      this.progress.step    = stepLabel
    },

    progressDone(successMessage = '') {
      clearInterval(this.progress._countdownTimer)
      this.progress.value     = 100
      this.progress.message   = successMessage || 'Saved successfully!'
      this.progress.step      = 'All done!'
      this.progress.done      = true
      this.progress.failed    = false
      this.progress.countdown = 5

      this.progress._countdownTimer = setInterval(() => {
        this.progress.countdown--
        if (this.progress.countdown <= 0) {
          this.closeProgress()
        }
      }, 1000)
    },

    progressFail(errorMessage = '') {
      clearInterval(this.progress._countdownTimer)
      this.progress.failed      = true
      this.progress.done        = false
      this.progress.step        = 'Failed.'
      this.progress.failMessage = errorMessage || 'Something went wrong.'
      this.progress.countdown   = 5

      this.progress._countdownTimer = setInterval(() => {
        this.progress.countdown--
        if (this.progress.countdown <= 0) {
          this.closeProgress()
        }
      }, 1000)
    },

    closeProgress() {
      clearInterval(this.progress._countdownTimer)
      this.progress.active      = false
      this.progress.done        = false
      this.progress.failed      = false
      this.progress.failMessage = ''
      this.progress.value       = 0
      this.progress.countdown   = 5
    },

  },
}
</script>

<style scoped>
.floating-alert-container {
  position: fixed;
  bottom: 1.25rem;
  left: 1.25rem;
  z-index: 9999;
  width: 360px;
  max-width: calc(100vw - 2rem);
  pointer-events: none;
}

.floating-alert {
  margin-bottom: 0.5rem;
  pointer-events: all;
}

/* ── Progress card ── */
.progress-alert {
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 6px;
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
}

.progress-alert__left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.progress-alert__message {
  font-weight: 600;
  font-size: 0.875rem;
  color: #2c2c2c;
}

.progress-alert__step {
  font-size: 0.75rem;
  color: #768192;
}

.progress-alert__fail {
  font-size: 0.75rem;
  color: #e55353;
  font-weight: 600;
}

.progress-alert__countdown {
  font-size: 0.7rem;
  color: #adb5bd;
}

/* ── Circle ── */
.progress-alert__right {
  position: relative;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle-svg {
  width: 56px;
  height: 56px;
  transform: rotate(-90deg);
  position: absolute;
  top: 0;
  left: 0;
}

.circle-track {
  fill: none;
  stroke: #e8e8e8;
  stroke-width: 3;
}

.circle-fill {
  fill: none;
  stroke: #321fdb;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dashoffset: 0;
  transition: stroke-dasharray 0.4s ease;
}

.circle-fill--done {
  stroke: #2eb85c;
}

.circle-fill--failed {
  stroke: #e55353;
}

.circle-percent {
  position: relative;
  font-size: 0.65rem;
  font-weight: 700;
  color: #321fdb;
  z-index: 1;
}

.percent-done {
  color: #2eb85c;
}

.percent-failed {
  color: #e55353;
}

/* ── Close button ── */
.progress-close-btn {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: #adb5bd;
  color: #fff;
  font-size: 0.55rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  padding: 0;
  line-height: 1;
}

.progress-close-btn:hover {
  background: #768192;
}

/* ── Transitions ── */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateX(-40px);
}

.slide-move {
  transition: transform 0.3s ease;
}
</style>